import { useEffect, useMemo, useState } from 'react';

import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { ProjectFeatures } from 'state/userSlice';

import { ToggleGroup } from 'features/common/ToggleGroup';
import { daysInRange } from 'features/common/datetimeUtils';
import { capitalizeFirstLetter } from 'features/common/utils';

import {
  COMPARISON_MODE_DAY,
  COMPARISON_MODE_MONTH,
  WorkflowType,
} from '../../appConstants';
import { useGetTypicalMonthsQuery } from '../../state/apiSlice';
import {
  selectComparison,
  selectComparisonEndDate,
  selectCurrentProjectInfo,
  selectTargetDate,
  selectTargetEndDate,
  selectWorkflow,
  setComparison,
  setComparisonStartEndDate,
} from '../../state/workflowSlice';
import { GenericDatePicker } from './DatePicker';
import { OverflowLabel } from './OverflowLabel';
import { TaskBarComponent } from './TaskBar';
import { GenericBaseMonthPicker } from './TypicalMonthPicker';

import styles from './Taskbar.module.css';

export function isComparisonMonth(comp: string) {
  if (!comp) {
    return false;
  }
  if (comp.split('-').length === 2) {
    return true;
  }
  return false;
}

export function comparisonToComparisonType(comp: string) {
  if (isComparisonMonth(comp) || !comp) {
    return COMPARISON_MODE_MONTH;
  } else {
    return COMPARISON_MODE_DAY;
  }
}

export function ComparisonPicker({
  forceMonthOnly = false,
  enableDayRange = false,
  radio = false,
  inline = false,
}: {
  forceMonthOnly?: boolean;
  enableDayRange?: boolean;
  radio?: boolean;
  inline?: boolean;
}) {
  const dispatch = useAppDispatch();
  const targetDate = useAppSelector(selectTargetDate);
  const targetEndDate = useAppSelector(selectTargetEndDate);
  const workflow = useAppSelector(selectWorkflow);
  const comparison = useAppSelector(selectComparison);
  const comparisonEndDate = useAppSelector(selectComparisonEndDate);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const [openDay, setOpenDay] = useState<boolean>(false);
  const [comparisonDay, setComparisonDay] = useState(
    comparison && !isComparisonMonth(comparison) ? comparison : undefined,
  );
  const [comparisonEndDay, setComparisonEndDay] = useState(comparisonEndDate);
  const [comparisonMonth, setComparisonMonth] = useState(
    comparison && isComparisonMonth(comparison) ? comparison : undefined,
  );

  const hasDayComparison =
    !forceMonthOnly &&
    !(
      WorkflowType.isTimelineIsh(workflow) &&
      !userProject.features.includes(ProjectFeatures.TIMELINE_DAY_COMPARISON)
    );

  const [comparisonType, setComparisonType] = useState(() =>
    hasDayComparison
      ? comparisonToComparisonType(comparison)
      : COMPARISON_MODE_MONTH,
  );

  const forbiddenDates = useMemo(() => {
    if (enableDayRange && comparisonEndDay) {
      return [];
    }
    return daysInRange(targetDate, targetEndDate);
  }, [targetDate, targetEndDate, comparisonEndDay, enableDayRange]);

  useEffect(() => {
    if (!hasDayComparison && comparisonType !== COMPARISON_MODE_MONTH) {
      setComparisonType(COMPARISON_MODE_MONTH);
    }
  }, [hasDayComparison, comparisonType]);

  // keep pickers in sync after workflow switches or URL changes.
  useEffect(() => {
    if (!comparison) {
      return;
    }

    if (isComparisonMonth(comparison)) {
      setComparisonType(COMPARISON_MODE_MONTH);
      setComparisonMonth(comparison);
      setComparisonEndDay(undefined);
    } else if (hasDayComparison) {
      setComparisonType(COMPARISON_MODE_DAY);
      setComparisonDay(comparison);
      setComparisonEndDay(enableDayRange ? comparisonEndDate : undefined);
    } else {
      setComparisonType(COMPARISON_MODE_MONTH);
    }
  }, [comparison, comparisonEndDate, hasDayComparison, enableDayRange]);

  useEffect(() => {
    if (comparisonType === COMPARISON_MODE_MONTH) {
      if (comparisonMonth && comparison !== comparisonMonth) {
        dispatch(setComparison(comparisonMonth));
      }
      return;
    }

    if (comparisonEndDay && enableDayRange) {
      if (
        comparisonDay !== comparison ||
        comparisonEndDate !== comparisonEndDay
      ) {
        dispatch(
          setComparisonStartEndDate({
            start: comparisonDay,
            end: comparisonEndDay,
          }),
        );
      }
      return;
    }

    if (comparisonDay && (comparison !== comparisonDay || comparisonEndDate)) {
      dispatch(setComparison(comparisonDay));
    }
  }, [
    comparisonDay,
    comparisonEndDay,
    comparisonMonth,
    comparisonType,
    enableDayRange,
  ]);

  return (
    <TaskBarComponent inline={inline}>
      <OverflowLabel>Comparison:</OverflowLabel>
      {!radio && hasDayComparison && (
        <ToggleGroup
          handleClick={(event: any) => {
            setComparisonType(event.target.value);
          }}
          value={comparisonType}
          values={[COMPARISON_MODE_MONTH, COMPARISON_MODE_DAY]}
          labelMap={{
            [COMPARISON_MODE_MONTH]: 'Month',
            [COMPARISON_MODE_DAY]: 'Day',
          }}
          horizontal
        />
      )}
      {radio && hasDayComparison && (
        <FormControl component="fieldset">
          <RadioGroup
            row
            name="comparisonType"
            value={comparisonType}
            onChange={(event) => setComparisonType(event.target.value)}
          >
            {[COMPARISON_MODE_MONTH, COMPARISON_MODE_DAY].map((comp) => (
              <FormControlLabel
                key={comp}
                sx={{
                  justifyContent: 'flex-start !important',
                  margin: 0,
                  '.MuiFormControlLabel-label': {
                    margin: 'auto 0',
                  },
                  '.MuiButtonBase-root': {
                    paddingLeft: '0',
                  },
                }}
                value={comp}
                control={<Radio />}
                label={capitalizeFirstLetter(comp)}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}
      <div className={hasDayComparison ? styles.task_bar_vpad_component : ''}>
        {comparisonType === COMPARISON_MODE_MONTH && (
          <GenericBaseMonthPicker
            querier={useGetTypicalMonthsQuery}
            value={comparisonMonth}
            setValue={setComparisonMonth}
          />
        )}
        {comparisonType === COMPARISON_MODE_DAY && (
          <GenericDatePicker
            open={openDay}
            setOpen={setOpenDay}
            enableDayRange={enableDayRange}
            targetDate={comparisonDay}
            endDate={enableDayRange ? comparisonEndDay : undefined}
            setNewTargetDate={(value) => {
              setComparisonDay(value);
              setComparisonEndDay(undefined);
            }}
            setNewRangeDate={(start, end) => {
              setComparisonDay(start);
              setComparisonEndDay(end);
            }}
            forbiddenDates={forbiddenDates}
            closeOnSelect
            fullWidth
          />
        )}
      </div>
    </TaskBarComponent>
  );
}
